import { useContext, useEffect, useState } from 'react';

import useIsMounted from '@swf/core/hooks/useIsMounted';
import useRequest from '@swf/core/hooks/useRequest';
import type {
  GetScheduledGamesResponse,
  HgClubInfo,
  HgScheduledGamesReply,
  ScheduledTourn
} from '@swf/generated/types/api/v0/SwHomeGames';
import { PokerContext, handleServerResponse } from '@swf/poker';

interface Props {
  clubs: HgClubInfo[];
  userClubsLoading: boolean;
  clubId?: number;
}

const useGetScheduledTourn = ({ clubs, userClubsLoading, clubId }: Props) => {
  const isMounted = useIsMounted();
  const request = useRequest();
  const [loading, setLoading] = useState(true);
  const [scheduled, setScheduled] = useState<ScheduledTourn[]>([]);
  const {
    state: { activeGameIds }
  } = useContext(PokerContext);

  useEffect(() => {
    if (userClubsLoading || !clubs.length || activeGameIds.length) {
      setLoading(false);
      return;
    }
    const body = clubId ? { clubId } : {};

    request({
      service: 'homeGames.getScheduledGamesRequest',
      body
    }).subscribe(({ body }) => {
      const {
        error,
        data: { tourns }
      } = handleServerResponse<GetScheduledGamesResponse, HgScheduledGamesReply>({
        response: body,
        accessor: 'scheduledGamesReply'
      });
      if (error || !tourns || tourns.length === 0) {
        setLoading(false);
        return;
      }

      // Scheduled tournaments are those where the user has registered
      // They are also sor†ed by starting time
      const scheduled = tourns
        .filter((tourn: ScheduledTourn) => tourn.registered)
        .sort(
          (prev: ScheduledTourn, next: ScheduledTourn) =>
            new Date(prev.startTime as string).getTime() -
            new Date(next.startTime as string).getTime()
        );
      setScheduled(scheduled);
      isMounted() && setLoading(false);
    });
  }, [activeGameIds.length, clubs.length, clubId, userClubsLoading, request, isMounted]);

  return { scheduled, loadingScheduled: userClubsLoading || loading };
};

export default useGetScheduledTourn;
